/**
 * Demo data for the prototype, tests and the 5,000-task performance check. Invented examples
 * only — never real task names. Dates are relative to "today" so the demo always looks alive.
 */
import {
  METHOD_TEMPLATE,
  addDays,
  computeDueAt,
  instantiateTemplate,
  todayInTz,
  buildDefaultReminderRows,
  BUILTIN_DEFAULT_REMINDERS,
  type NotificationLog,
  type Priority,
  type Profile,
  type Reminder,
  type Task,
  type TaskTag,
} from '@karya/core';
import { newId } from '@/lib/ids';
import type { KaryaDB } from './dexie';
import { fallbackProfile } from '@/data/queries';

export const DEMO_USER_ID = '00000000-0000-7000-8000-00000000d3e0';

type Seed = [
  name: string,
  space: number,
  list: number,
  dueOffset: number | null,
  priority: Priority | null,
  status: number,
  time?: string,
];

const TASKS: Seed[] = [
  ['Review hiring plan draft', 0, 0, 0, 1, 1],
  ['Send invoice to Meridian Labs', 0, 1, 0, 2, 0],
  ['Call the bank about the card', 2, 1, 0, 2, 0, '17:00'],
  ['Water the balcony plants', 1, 1, 0, 3, 0],
  ['Follow up with the landlord', 1, 2, 0, 3, 2],
  ['Draft Q4 plan for the studio', 0, 0, -5, 1, 1],
  ['Renew car insurance', 2, 0, -3, 2, 0],
  ['Reply to Priya about the venue', 2, 2, -1, 3, 2],
  ['Book dentist appointment', 2, 1, 2, 4, 0],
  ['Weekly review', 0, 2, 4, 3, 0],
  ['Publish changelog for v2', 3, 0, 5, 2, 0],
  ['Plan offsite agenda', 0, 0, 13, 3, 0],
  ['Migrate invoices to the new tool', 0, 0, 1, 3, 1],
  ['Contract from the agency', 0, 2, 4, 3, 2],
  ['Quarterly taxes', 2, 0, 18, 1, 0],
  ['Car service', 2, 3, 21, 4, 0],
  ['Clean up the photo library', 2, 3, null, 4, 0],
  ['Learn the new budgeting app', 2, 3, null, null, 0],
  ['Order printer ink', 1, 1, 1, 3, 0],
  ['Ask the society about parking', 1, 2, 6, 3, 2],
  ['Sketch landing page ideas', 3, 1, 3, 3, 1],
  ['Daily - check the support inbox', 0, 1, 0, 3, 0],
  ['Pay electricity bill', 1, 0, -9, 2, 3],
  ['Send birthday gift to Mum', 2, 0, -12, 2, 3],
  ['Set up the new laptop', 0, 1, -2, 3, 3],
  ['Write blog post outline', 3, 3, -20, 4, 3],
];

export async function seedDemo(
  db: KaryaDB,
  tz = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Promise<void> {
  if ((await db.spaces.count()) > 0) return;
  const now = new Date();
  const ts = now.toISOString();
  const today = todayInTz(tz, now);
  const profile: Profile = {
    ...fallbackProfile(DEMO_USER_ID, 'demo@karya.app'),
    display_name: 'Ashish',
    timezone: tz,
    day_start_time: '10:30',
    onboarded_at: ts,
  };
  const inst = instantiateTemplate(METHOD_TEMPLATE, {
    userId: DEMO_USER_ID,
    newId,
    now,
    spaceNames: ['Work', 'Home', 'Personal', 'Side project'],
    workspaceName: 'My Workspace',
  });
  profile.default_quick_add_list_id =
    inst.lists.find((l) => l.space_id === inst.spaces[2]!.id && l.name === 'Quick Wins')?.id ??
    null;
  const base = { user_id: DEMO_USER_ID, created_at: ts, updated_at: ts, deleted_at: null };
  const tasks: Task[] = [];
  const taskTags: TaskTag[] = [];
  const reminders: Reminder[] = [];
  TASKS.forEach(([name, si, li, off, priority, st, time], i) => {
    const space = inst.spaces[si]!;
    const lists = inst.lists.filter((l) => l.space_id === space.id);
    const list = lists[li]!;
    const statuses = inst.statuses
      .filter((s) => s.space_id === space.id)
      .sort((a, b) => a.position - b.position);
    const status = statuses[st] ?? statuses[0]!;
    const due_date = off === null ? null : addDays(today, off);
    const due_time = time ?? null;
    const due_at = computeDueAt(due_date, due_time, tz, '10:30');
    const created = new Date(now.getTime() - (30 - i) * 86_400_000).toISOString();
    const task: Task = {
      ...base,
      created_at: created,
      id: newId(),
      list_id: list.id,
      parent_task_id: null,
      name,
      description_md:
        i === 0
          ? 'Check the headcount numbers against the budget sheet, then send comments by Friday.\n\n- Engineering: 2 roles\n- Design: 1 contract role'
          : null,
      status_id: status.id,
      priority,
      start_date: null,
      start_time: null,
      due_date,
      due_time,
      due_at,
      completed_at:
        st === 3 ? new Date(now.getTime() - (i % 5) * 86_400_000 - 3_600_000).toISOString() : null,
      position: (i + 1) * 1024,
      recurrence: name.startsWith('Daily')
        ? {
            mode: 'on_complete',
            basis: 'schedule',
            create_new_task: true,
            reset_status_id: null,
            end: { type: 'never' },
            skip_weekends: false,
            copy_subtasks: true,
            copy_checklists: 'reset',
            preset: 'daily',
          }
        : name === 'Weekly review'
          ? {
              mode: 'on_complete',
              basis: 'schedule',
              create_new_task: true,
              reset_status_id: null,
              end: { type: 'never' },
              skip_weekends: false,
              copy_subtasks: true,
              copy_checklists: 'reset',
              preset: 'weekly',
            }
          : null,
      recurrence_rrule: name.startsWith('Daily')
        ? 'FREQ=DAILY;INTERVAL=1'
        : name === 'Weekly review'
          ? 'FREQ=WEEKLY;INTERVAL=1;BYDAY=MO'
          : null,
      recurrence_parent_id: null,
      archived_at: null,
      source_url: null,
      clickup_id: null,
      reminders_initialized: true,
    };
    tasks.push(task);
    const tag = inst.tags.filter((t) => t.space_id === space.id)[li];
    if (tag && i % 5 !== 4) taskTags.push({ task_id: task.id, tag_id: tag.id, ...base });
    if (due_at && st !== 3) {
      for (const r of buildDefaultReminderRows(
        BUILTIN_DEFAULT_REMINDERS,
        'global_default',
        due_at,
        now,
      )) {
        reminders.push({
          ...base,
          id: newId(),
          task_id: task.id,
          title: null,
          nag_every_minutes: null,
          nag_until: null,
          nag_count: null,
          snoozed_until: null,
          ...r,
        });
      }
    }
  });
  // Subtasks under the first task.
  const parent = tasks[0]!;
  const parentStatuses = inst.statuses
    .filter((s) => s.space_id === inst.lists.find((l) => l.id === parent.list_id)!.space_id)
    .sort((a, b) => a.position - b.position);
  [
    ['Shortlist three candidates', 0, 0],
    ['Book interview rooms', 3, -1],
  ].forEach(([name, st, off], i) => {
    tasks.push({
      ...parent,
      id: newId(),
      parent_task_id: parent.id,
      name: name as string,
      description_md: null,
      status_id: parentStatuses[st as number]!.id,
      priority: 2,
      due_date: addDays(today, off as number),
      due_at: computeDueAt(addDays(today, off as number), null, tz, '10:30'),
      completed_at: st === 3 ? ts : null,
      position: (i + 1) * 1024,
      recurrence: null,
      recurrence_rrule: null,
    });
  });
  // A few inbox notifications.
  const notes: NotificationLog[] = [
    ['reminder', 'Call the bank about the card', 'Due today at 5pm · 1 hour before', 30, null],
    ['nag', 'Renew car insurance', 'Overdue · reminder repeats every 2 hours', 120, null],
    ['daily_summary', 'Your day', '5 due today · 3 overdue · 6 this week', 300, ts],
    ['recurrence_created', 'Weekly review', 'Next one created, due Monday', 900, ts],
  ].map(([kind, title, body, minsAgo, read]) => ({
    ...base,
    id: newId(),
    reminder_id: null,
    task_id: tasks.find((t) => t.name === title)?.id ?? null,
    kind: kind as NotificationLog['kind'],
    channel: 'push',
    title: title as string,
    body: body as string,
    url: '/',
    sent_at: new Date(now.getTime() - (minsAgo as number) * 60_000).toISOString(),
    delivered: true,
    error: null,
    read_at: read as string | null,
    acted: null,
  }));
  await db.transaction(
    'rw',
    [
      db.profiles,
      db.workspaces,
      db.spaces,
      db.lists,
      db.statuses,
      db.tags,
      db.tasks,
      db.task_tags,
      db.reminders,
      db.notifications_log,
    ],
    async () => {
      await db.profiles.put(profile);
      if (inst.workspace) await db.workspaces.put(inst.workspace);
      await db.spaces.bulkPut(inst.spaces);
      await db.lists.bulkPut(inst.lists);
      await db.statuses.bulkPut(inst.statuses);
      await db.tags.bulkPut(inst.tags);
      await db.tasks.bulkPut(tasks);
      await db.task_tags.bulkPut(taskTags);
      await db.reminders.bulkPut(reminders);
      await db.notifications_log.bulkPut(notes);
    },
  );
}

/** Adds `count` synthetic tasks across the demo lists (performance check). */
export async function seedMany(
  db: KaryaDB,
  count: number,
  tz = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Promise<void> {
  const lists = await db.lists.toArray();
  const statuses = await db.statuses.toArray();
  const now = new Date();
  const today = todayInTz(tz, now);
  const ts = now.toISOString();
  const words = [
    'Review',
    'Plan',
    'Email',
    'Call',
    'Draft',
    'Fix',
    'Order',
    'Check',
    'Book',
    'Update',
    'Prepare',
    'Follow up on',
  ];
  const things = [
    'budget',
    'report',
    'invoice',
    'meeting',
    'design',
    'contract',
    'garden',
    'car',
    'trip',
    'slides',
    'backup',
    'taxes',
  ];
  const rows: Task[] = [];
  for (let i = 0; i < count; i++) {
    const list = lists[i % lists.length]!;
    const sts = statuses
      .filter((s) => s.space_id === list.space_id)
      .sort((a, b) => a.position - b.position);
    const st = sts[i % 7 === 0 ? 3 : i % 3]!;
    const off = i % 11 === 0 ? null : (i % 41) - 10;
    const due_date = off === null ? null : addDays(today, off);
    rows.push({
      id: newId(),
      user_id: DEMO_USER_ID,
      created_at: ts,
      updated_at: ts,
      deleted_at: null,
      list_id: list.id,
      parent_task_id: null,
      name: `${words[i % words.length]} ${things[(i * 7) % things.length]} #${i + 1}`,
      description_md: null,
      status_id: st.id,
      priority: (i % 5 || null) as Priority | null,
      start_date: null,
      start_time: null,
      due_date,
      due_time: null,
      due_at: computeDueAt(due_date, null, tz, '10:30'),
      completed_at: st.type === 'closed' ? ts : null,
      position: 100000 + i * 1024,
      recurrence: null,
      recurrence_rrule: null,
      recurrence_parent_id: null,
      archived_at: null,
      source_url: null,
      clickup_id: null,
      reminders_initialized: true,
    });
  }
  await db.tasks.bulkPut(rows);
}
